(() => {
  if (window.fpRewardsCartInitialized) return;
  const bar = document.querySelector('[data-fp-rewards-cart]');
  if (!bar) return;
  window.fpRewardsCartInitialized = true;
  document.body.append(bar);
  const root = window.Shopify?.routes?.root || '/';
  const message = bar.querySelector('[data-rewards-message]');
  const progress = bar.querySelector('[data-rewards-progress]');
  const link = bar.querySelector('[data-rewards-cart-link]');
  const threshold = Number(bar.dataset.shippingThreshold) || 0;
  let busy = false;
  let revision = 0;
  let timer;
  let previewWrapper = null;
  const previewResizeObserver = window.ResizeObserver ? new ResizeObserver(previewOffset) : null;
  const money = (cents, currency) => new Intl.NumberFormat('es-MX', {
    style: 'currency', currency, minimumFractionDigits: 2
  }).format(cents / 100);

  function reserveSpace() {
    const height = bar.hidden ? 0 : Math.ceil(bar.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--fp-rewards-cart-height', `${height}px`);
  }

  // Keep the development toolbar visible without allowing it to cover the CTA.
  function previewOffset() {
    let offset = 0;
    // Measure the visible host, not its full-screen cross-origin iframe.
    const wrapper = document.getElementById('PBarNextFrameWrapper');
    if (wrapper !== previewWrapper) {
      previewResizeObserver?.disconnect();
      previewWrapper = wrapper;
      if (wrapper) previewResizeObserver?.observe(wrapper);
    }
    document.querySelectorAll('#PBarNextFrameWrapper, #preview-bar-iframe, #ShopifyPreviewBar, iframe[src*="preview_bar"]').forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.height > 0 && rect.height < 180 && rect.top < innerHeight && rect.bottom >= innerHeight - 2) {
        offset = Math.max(offset, innerHeight - rect.top);
      }
    });
    document.documentElement.style.setProperty('--fp-rewards-preview-bottom', `${offset}px`);
  }

  function render(cart) {
    if (!Array.isArray(cart.items) || !Number.isFinite(cart.total_price)) throw new Error('Invalid cart');
    const currency = cart.currency || 'MXN';
    const showProgress = currency === 'MXN' && threshold > 0;
    const remaining = Math.max(0, threshold - cart.total_price);
    bar.hidden = cart.item_count === 0;
    bar.classList.toggle('is-complete', showProgress && remaining === 0);
    message.textContent = showProgress
      ? remaining ? `Te faltan ${money(remaining, currency)} para envío gratis` : '¡Tienes envío gratis!'
      : `${cart.item_count} ${cart.item_count === 1 ? 'producto en tu carrito' : 'productos en tu carrito'}`;
    progress.hidden = !showProgress;
    progress.value = showProgress ? Math.min(100, cart.total_price / threshold * 100) : 0;
    link.textContent = `Ver carrito · ${money(cart.total_price, currency)}`;
    document.body.classList.toggle('fp-rewards-cart-visible', !bar.hidden);
    document.documentElement.classList.toggle('fp-rewards-cart-visible', !bar.hidden);
    document.querySelectorAll('[data-fp-variant]').forEach(button => {
      if (button.disabled && button.getAttribute('aria-busy') !== 'true') return;
      const quantity = cart.items.filter(item => String(item.variant_id) === button.dataset.fpVariant)
        .reduce((total, item) => total + item.quantity, 0);
      button.textContent = quantity ? `${quantity} ✓ +` : '+';
      button.classList.toggle('is-added', quantity > 0);
      button.setAttribute('aria-label', `Agregar ${button.dataset.fpProductTitle}${quantity ? `; ${quantity} en el carrito` : ''}`);
    });
    document.querySelectorAll('.fp2-cart').forEach(anchor => {
      let badge = anchor.querySelector('.fp2-cart__badge');
      if (!badge && cart.item_count) {
        badge = document.createElement('span');
        badge.className = 'fp2-cart__badge';
        anchor.append(badge);
      }
      if (badge) {
        badge.hidden = !cart.item_count;
        badge.textContent = String(cart.item_count);
      }
    });
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = String(cart.item_count);
      el.hidden = !cart.item_count;
    });
    reserveSpace();
    previewOffset();
  }

  async function refresh() {
    const version = ++revision;
    const response = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    if (version === revision) render(cart);
    return cart;
  }

  function scheduleRefresh() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (!busy) refresh().catch(() => {});
    }, 100);
  }

  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-fp-rewards-add]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    busy = true;
    ++revision; // Discard reads that started before this mutation.
    const button = form.querySelector('button');
    const error = form.closest('.fp-plp-card').querySelector('[data-fp-rewards-error]');
    const previous = button.textContent;
    let added = false;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = '…';
    error.hidden = true;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json' }, body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'No se pudo agregar el producto.');
      added = true;
      const cart = await refresh();
      document.dispatchEvent(new CustomEvent('fp:cart-updated', { detail: { cart } }));
    } catch (err) {
      button.textContent = previous;
      error.textContent = added
        ? 'Producto agregado. No pudimos actualizar el resumen; revisa tu carrito antes de agregar otra vez.'
        : err.message === 'Failed to fetch'
          ? 'No pudimos confirmar el agregado. Revisa tu carrito antes de reintentar.'
          : err.message;
      error.hidden = false;
      refresh().catch(() => {});
    } finally {
      busy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }, true);

  // Existing theme cart requests use jQuery; observe success, never replace its API.
  function connectThemeCart() {
    if (!window.jQuery || bar.dataset.ajaxConnected) return;
    bar.dataset.ajaxConnected = 'true';
    window.jQuery(document).on('ajaxSuccess.fpRewardsCart', (_event, _xhr, settings) => {
      if (/\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(settings.url || '')) scheduleRefresh();
    });
  }
  connectThemeCart();
  window.addEventListener('load', () => { connectThemeCart(); previewOffset(); });
  window.addEventListener('pageshow', scheduleRefresh);
  window.addEventListener('focus', scheduleRefresh);
  window.addEventListener('resize', previewOffset);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(); });
  document.addEventListener('fp:cart-updated', scheduleRefresh);
  if (window.ResizeObserver) new ResizeObserver(reserveSpace).observe(bar);
  let offsetFrame = 0;
  new MutationObserver(() => {
    cancelAnimationFrame(offsetFrame);
    offsetFrame = requestAnimationFrame(previewOffset);
  }).observe(document.body, { childList: true, subtree: true });
  refresh().catch(() => {});
})();
